/**
 * The web app's entry (unit 26A). One query client for the app: a read that
 * answers 401 means the session ended, and the app goes back to sign-in.
 */
import "./styles/fonts.css";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/prose.css";

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { ApiError } from "./api/query";
import { App } from "./app/App";
import { isSignedOut, SESSION } from "./app/session";

const signedOut = (error: unknown) => {
  if (isSignedOut(error)) client.setQueryData(SESSION, null);
};

const isFinal = (error: unknown) =>
  error instanceof ApiError && (error.status < 500 || error.status === 503);

const client: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: signedOut }),
  mutationCache: new MutationCache({ onError: signedOut }),
  defaultOptions: {
    queries: {
      // A 4xx will not change by asking again, and a 503 is the server's own
      // words ("Google is not connected"); a dropped signal might.
      retry: (count, error) => !isFinal(error) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

const root = document.getElementById("app");
if (!root) throw new Error("index.html has no #app");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={client}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
